import { useEffect, useState } from 'react';
import { PhoneIcon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useVersionedApi } from '../utils/versionedApi';
import { safeStorage } from '../utils/safeStorage';

// Only ARMHS clinical staff and their supervisors need the after-hours on-call
// contact — not admin/billing/support/PCA/etc.
const ON_CALL_PROVIDER_TYPES = ['MHP', 'Therapist', 'Clinical_Supervisor', 'MH_Supervisor'];

/**
 * OnCallPopup — shows the current on-call supervisor once per browser session
 * when staff open AraSync, so after-hours ARMHS staff can quickly see who to
 * reach (BUG-A4B9B2DB). Only shown to MHPs, therapists, and clinical/MH
 * supervisors. Dismissible; suppressed for the rest of the session via
 * sessionStorage. Renders nothing when no on-call person is set.
 */
export default function OnCallPopup() {
  const api = useVersionedApi('v1');
  const [onCall, setOnCall] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Restrict to ARMHS clinical staff / supervisors by providerType.
    let providerType = null;
    try { providerType = JSON.parse(safeStorage.getItem('evv_user') || '{}').providerType; } catch { /* private mode */ }
    if (!ON_CALL_PROVIDER_TYPES.includes(providerType)) return;

    // Only once per session.
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem('onCallPopupShown') === '1'; } catch { /* private mode */ }
    if (alreadyShown) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/on-call');
        const data = res.data?.data;
        if (!cancelled && data && data.name) {
          setOnCall(data);
          setOpen(true);
        }
      } catch {
        // Non-critical — never block the app if this fails to load.
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const dismiss = () => {
    setOpen(false);
    try { sessionStorage.setItem('onCallPopupShown', '1'); } catch { /* ignore */ }
  };

  if (!open || !onCall) return null;

  const telHref = onCall.phone ? `tel:${onCall.phone.replace(/[^\d+]/g, '')}` : null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <h2 className="text-base font-semibold">On-Call Supervisor</h2>
          <button onClick={dismiss} className="p-1 rounded hover:bg-white/20" aria-label="Dismiss">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            For ARMHS clients seen outside regular business hours, contact the current on-call supervisor:
          </p>
          <div className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/40">
            <UserCircleIcon className="h-9 w-9 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900 dark:text-white">{onCall.name}</p>
              {onCall.phone && (
                <a href={telHref} className="mt-1 inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  <PhoneIcon className="h-4 w-4" /> {onCall.phone}
                </a>
              )}
              {onCall.notes && (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">{onCall.notes}</p>
              )}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button onClick={dismiss} className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
